import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef({})

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current[id])
    delete timers.current[id]
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const toast = useCallback(
    ({ message, action, duration = 3500 }) => {
      const id = Date.now().toString()
      setToasts((t) => [...t.slice(-4), { id, message, action }])
      // duration === 0 → persistent (caller dismisses via action/X)
      if (duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration)
      return id
    },
    [dismiss]
  )

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none"
      >
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.97, transition: { duration: 0.15, ease: 'easeOut' } }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="pointer-events-auto flex items-center gap-3 bg-surface border border-border rounded-xl px-4 py-2.5 shadow-2xl min-w-[200px] max-w-[380px]"
            >
              <span className="text-sm text-text flex-1 leading-snug">{t.message}</span>
              {t.action && (
                <button
                  onClick={() => { t.action.onClick(); dismiss(t.id) }}
                  className="text-xs font-semibold text-accent hover:opacity-80 transition-opacity active:scale-[0.97] flex-shrink-0 whitespace-nowrap"
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="text-text-faint hover:text-text-muted transition-colors flex-shrink-0 active:scale-[0.94]"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}
